import { CommonModule } from "@angular/common";
import { Component, computed, effect, inject, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router, RouterLink } from "@angular/router";
import { AccountMarketplace } from "@front/m19-api-client";
import { AccountGroup, NotificationEx } from "@front/m19-models";
import {
  AccountSelectionService,
  AuthService,
  BillingService,
  NotificationService,
  UserSelectionService,
} from "@front/m19-services";
import {
  DropdownMenuItem,
  IAccordionComponent,
  IBadgeComponent,
  IButtonComponent,
  IDropdownComponent,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import { combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import { OnboardingCtaComponent } from "./onboarding-cta.component";
import { LogoComponent } from "@m19-board/shared/logo/logo.component";
import { advertisingMenu, dashboardMenu, getSettingsMenu, keywordMenu, NavItem, productMenu } from "./sidebar-items";
import { EnvironmentService } from "@m19-board/services/environment.service";
import { PageLayoutService } from "@m19-board/containers/page-layout/page-layout.service";

@Component({
  selector: "board-sidebar",
  standalone: true,
  imports: [
    CommonModule,
    IAccordionComponent,
    IButtonComponent,
    TranslocoDirective,
    RouterLink,
    IBadgeComponent,
    OnboardingCtaComponent,
    IDropdownComponent,
    LogoComponent,
  ],
  templateUrl: "./board-sidebar.component.html",
})
export class BoardSidebarComponent implements OnInit {
  router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly layoutSelector = inject(LayoutSelectorService);
  private readonly billingService = inject(BillingService);
  private readonly notificationService = inject(NotificationService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly pageLayoutService = inject(PageLayoutService);

  readonly environment = this.environmentService.getEnvironmentConfig();
  readonly releaseNotesUrl = this.environmentService.getEnvironmentConfig().releaseNotesUrl;
  readonly BoardType = BoardType;
  readonly NAV_ITEM_CLASS =
    "flex h-7 max-h-7 min-h-7 cursor-pointer select-none items-center gap-1.5 rounded-md bg-transparent px-2 " +
    "no-underline transition-colors duration-100 hover:bg-gray-100 hover:no-underline";

  notifications = toSignal(
    combineLatest([
      this.notificationService.getNotifications$,
      this.accountSelectionService.accountGroupSelection$,
      this.accountSelectionService.accountMarketplacesSelection$,
    ]).pipe(
      map(
        ([notifications, accountGroup, accountMarketplaces]: [
          NotificationEx[],
          AccountGroup,
          AccountMarketplace[],
        ]) => {
          const accountIds = accountGroup.getAccountMarketplaces().map((a) => a.accountId);
          return this.filterNotifications(notifications, accountIds, accountMarketplaces);
        },
      ),
    ),
  );

  notifCountLabel = computed(() => {
    const count = this.notifications()?.length;
    return count && count > 99 ? "99+" : count?.toString();
  });

  isBillingOwnerOfActiveSubscription = toSignal(
    this.billingService.organizationOnwer$.pipe(map((o) => !!o?.hasActiveSubscription())),
    {
      requireSync: true,
    },
  );
  isCollapsed = this.userSelectionService.sidebarCollapsed;
  isBetaTester = toSignal<boolean>(this.authService.loggedUser$.pipe(map((u) => !!u.betaTester)), {
    requireSync: true,
  });

  currentUrl = toSignal(this.pageLayoutService.navigationEvents$.pipe(map((event) => event.url)));
  openedItemId = signal<string | undefined>(undefined);
  selectedItemId = computed(() => {
    if (!this.currentUrl()) return undefined;

    for (const section of this.menu()) {
      for (const item of section.children ?? []) {
        if (item?.url && this.currentUrl()?.startsWith(item.url)) return item.id;
        if (item?.children) {
          const child = item.children.find((child) => {
            return child?.url && this.currentUrl()?.startsWith(child.url);
          });
          if (child) {
            return this.openedItemId() === item.id ? child.id : item.id;
          }
        }
      }
    }
    return undefined;
  });

  boardType = this.layoutSelector.getBoardType();
  whitelabelContactUsLink = this.layoutSelector.getContact();

  private readonly referralProgram = this.layoutSelector.getReferralProgram();
  private readonly settingsMenu = getSettingsMenu(this.releaseNotesUrl, this.referralProgram);

  readonly filteredSettingsMenu = computed<DropdownMenuItem[][]>(() => {
    return this.settingsMenu.map((section) =>
      section.filter((item) => {
        if (item.id === "referral-program" && item.href) {
          return true;
        } else if (item.id === "billing") {
          return (
            this.boardType == BoardType.M19 ||
            this.boardType == BoardType.WHITELABEL_SELFSERVICE ||
            this.isBillingOwnerOfActiveSubscription()
          );
        }
        return true;
      }),
    );
  });

  menu = computed<NavItem[]>(() =>
    this.getNavItems().map((section) => this.filterItemsSection(section, this.isBetaTester())),
  );

  constructor() {
    effect(() => {
      localStorage.setItem("sidebar-opened-item", this.openedItemId() ?? "");
      localStorage.setItem("sidebar-collapsed", this.isCollapsed().toString());
    });
  }

  ngOnInit(): void {
    this.openedItemId.set(localStorage.getItem("sidebar-opened-item") ?? undefined);

    this.settingsMenu.push([
      {
        id: "logout",
        label: "page-title.logout",
        icon: "icon-[lucide--log-out]",
        onClick: () => this.logout(),
      },
    ]);
  }

  getNavItems() {
    return [dashboardMenu, advertisingMenu, productMenu, keywordMenu];
  }

  private logout() {
    this.authService.logout();
    this.router.navigate(["/login"]);
  }

  private filterItemsSection(items: NavItem, isBetaTester: boolean): NavItem {
    return {
      ...items,
      children: this.filterNavItems(items.children ?? [], isBetaTester),
    };
  }

  private filterNavItems(items: NavItem[], isBetaTester: boolean): NavItem[] {
    return items
      .filter((item) => this.shouldDisplayItem(item, isBetaTester))
      .map((item) => {
        if (item.children) {
          return {
            ...item,
            children: this.filterNavItems(item.children, isBetaTester),
          };
        }
        return item;
      });
  }

  private shouldDisplayItem(item: NavItem, isBetaTester: boolean): boolean {
    if (item.onlyBeta && !isBetaTester) {
      return false;
    }

    const isEnvironmentMatching =
      item.environment === this.environment.environment ||
      (item.environment === "demo" && this.environment.environment === "dev");

    return !(item.environment && !isEnvironmentMatching);
  }

  private filterNotifications(
    notifications: NotificationEx[],
    accountIds: string[],
    accountMarketplaces: AccountMarketplace[],
  ) {
    const result = [];
    for (const notification of notifications) {
      if (notification.dismissed) {
        continue; // do not display dismissed notif
      }
      if (notification.isCritical) {
        result.push(notification); // display critical notif
        continue;
      }
      if (
        notification.marketplace &&
        accountMarketplaces.findIndex(
          (am) => am.accountId == notification.accountId && am.marketplace == notification.marketplace,
        ) > -1
      ) {
        // display notification of the selected marketplaces
        result.push(notification);
        continue;
      }
      if (!notification.marketplace && accountIds.includes(notification.accountId)) {
        // display notification of the selected marketplaces
        result.push(notification);
      }
    }
    return result;
  }

  toggleItem(id: string) {
    this.openedItemId.set(this.openedItemId() === id ? undefined : id);
  }
}
